import { useEffect, useState } from "react";
import api from "../lib/api";
import jsPDF from "jspdf";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Contact, History, Download, Plus, Trash2, Pencil } from "lucide-react";

export default function Customers() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [snapshots, setSnapshots] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });

  const load = () => api.get("/customers").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/backup/stats").then(({ data }) => setSnapshots(data.snapshots)).catch(() => {});
  }, []);

  const saveCustomer = async () => {
    if (!form.name && !form.phone && !form.email) return toast.error("Inserisci almeno il nome");
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success("Cliente salvato"); setNewOpen(false); setEditId(null); setForm({ name: "", phone: "", email: "" }); load();
    } catch { toast.error("Impossibile salvare il cliente"); }
  };
  const openEdit = (c) => { setEditId(c.id); setForm({ name: c.name || "", phone: c.phone || "", email: c.email || "" }); setNewOpen(true); };
  const removeCustomer = async (id) => {
    if (!window.confirm("Eliminare questo cliente?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Cliente eliminato"); load(); }
    catch { toast.error("Impossibile eliminare"); }
  };

  const backup = async () => {
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "backup-dogana.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
    } catch { toast.error("Backup disponibile solo per l'admin"); }
  };

  const generatePDF = async () => {
    let biz = { name: "Dogana 2.0", address: "", vat: "", phone: "" };
    try { biz = (await api.get("/settings/business")).data; } catch { /* noop */ }
    const logo = await new Promise((res) => {
      fetch("/logo-dogana.png").then((r) => r.blob()).then((b) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(null); fr.readAsDataURL(b); }).catch(() => res(null));
    });
    const doc = new jsPDF();
    if (logo) { try { doc.addImage(logo, "PNG", 12, 9, 20, 20); } catch { /* noop */ } }
    const hx = logo ? 37 : 12;
    doc.setFontSize(14); doc.setFont(undefined, "bold"); doc.text(biz.name || "Dogana 2.0", hx, 16);
    doc.setFontSize(9); doc.setFont(undefined, "normal"); let hy = 21;
    if (biz.address) { doc.text(biz.address, hx, hy); hy += 4.5; }
    const l2 = [biz.vat && `P.IVA: ${biz.vat}`, biz.phone && `Tel: ${biz.phone}`].filter(Boolean).join("    ");
    if (l2) doc.text(l2, hx, hy);
    doc.setDrawColor(180); doc.line(12, 32, 198, 32);
    doc.setFontSize(13); doc.setFont(undefined, "bold"); doc.text(`Elenco Clienti — ${new Date().toLocaleDateString("it-IT")}`, 12, 40);
    let y = 50;
    doc.setFontSize(9); doc.setFont(undefined, "bold");
    doc.text("Cliente", 12, y); doc.text("Contatti", 82, y); doc.text("Pren.", 158, y); doc.text("Ultima visita", 175, y);
    y += 2; doc.setDrawColor(220); doc.line(12, y, 198, y); y += 5;
    doc.setFont(undefined, "normal");
    items.forEach((c) => {
      if (y > 285) { doc.addPage(); y = 15; }
      doc.text(String(c.name || "—").slice(0, 42), 12, y);
      doc.text(String([c.phone, c.email].filter(Boolean).join(" · ")).slice(0, 42), 82, y);
      doc.text(String(c.visits || 0), 160, y);
      doc.text(String(c.last_visit || "—"), 175, y);
      y += 6;
    });
    if (items.length === 0) doc.text("Nessun cliente registrato.", 12, y);
    doc.save("elenco-clienti.pdf");
    toast.success("PDF clienti generato");
  };

  return (
    <div>
      <PageHeader title="Clienti (CRM)" subtitle="Anagrafica clienti dalle prenotazioni e inserimento manuale" testid="customers-header">
        {user?.role === "admin" && (
          <Button variant="outline" onClick={backup} className="rounded-full" data-testid="backup-button">
            <Download className="w-4 h-4 mr-1" /> Scarica backup {snapshots != null ? `(${snapshots} salvataggi)` : ""}
          </Button>
        )}
        {canManage && (
          <Button onClick={generatePDF} variant="outline" className="rounded-full" data-testid="print-customers-button">
            <Download className="w-4 h-4 mr-1" /> Stampa PDF
          </Button>
        )}
        {canManage && (
          <Button onClick={() => { setEditId(null); setForm({ name: "", phone: "", email: "" }); setNewOpen(true); }} className="rounded-full" data-testid="add-customer-button">
            <Plus className="w-4 h-4 mr-1" /> Nuovo cliente
          </Button>
        )}
      </PageHeader>
      <Card className="rounded-xl overflow-hidden">
        <Table data-testid="customers-table">
          <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Contatti</TableHead><TableHead className="text-center">Prenotazioni</TableHead><TableHead>Ultima visita</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nessun cliente ancora. Si popola con le prenotazioni.</TableCell></TableRow>}
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell className="font-medium flex items-center gap-2"><Contact className="w-4 h-4 text-primary" />{c.name || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{[c.phone, c.email].filter(Boolean).join(" · ") || "—"}</TableCell>
                <TableCell className="text-center">{c.visits || 0}</TableCell>
                <TableCell>{c.last_visit || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setSel(c)} data-testid={`customer-history-${c.id}`}><History className="w-4 h-4 mr-1" /> Storico</Button>
                  {canManage && <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="w-4 h-4 text-primary" /></Button>}
                  {canManage && <Button size="icon" variant="ghost" onClick={() => removeCustomer(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="customer-history-dialog">
          <DialogHeader><DialogTitle>Storico · {sel?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {(sel?.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="flex justify-between text-sm border-b border-border/60 pb-2">
                <span>{h.date} · {h.time} · {h.guests} coperti</span>
                <span className="text-xs bg-secondary px-2 py-0.5 rounded-full">{h.source}</span>
              </div>
            ))}
            {(!sel?.history || sel.history.length === 0) && <p className="text-sm text-muted-foreground">Nessuna prenotazione registrata.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={(o) => { setNewOpen(o); if (!o) setEditId(null); }}>
        <DialogContent data-testid="new-customer-dialog">
          <DialogHeader><DialogTitle>{editId ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome cliente" data-testid="customer-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefono" data-testid="customer-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="customer-email-input" />
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={saveCustomer} className="rounded-full" data-testid="save-customer-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
